/**
 * guidanceOverlay.ts
 *
 * Shared drawing functions for the silhouette guidance overlay.
 * Used by PoseDebugPage (debug viewer) and ScanV2Page (production scan flow).
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roundedRectPath(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.quadraticCurveTo(x + w, y, x + w, y + rr);
  c.lineTo(x + w, y + h - rr);
  c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  c.lineTo(x + rr, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - rr);
  c.lineTo(x, y + rr);
  c.quadraticCurveTo(x, y, x + rr, y);
}

export function getHeadAnchor(kpPx: [number, number][]): [number, number] {
  const headIdx = [0, 1, 2, 3, 4];
  const pts = headIdx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length === 0) return kpPx[0] ?? [0, 0];
  const sx = pts.reduce((acc, [x]) => acc + x, 0);
  const sy = pts.reduce((acc, [, y]) => acc + y, 0);
  return [sx / pts.length, sy / pts.length];
}

export function getPoseBBox(kpPx: [number, number][]) {
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  const pts = idx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX, maxX, minY, maxY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

// ---------------------------------------------------------------------------
// Silhouette drawing
// ---------------------------------------------------------------------------

export function drawFrontSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult = 1,
  torsoWidthMult = 1
) {
  const sx = scale * torsoWidthMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Head
  ctx.beginPath();
  ctx.arc(headX, headY, hr, 0, Math.PI * 2);
  ctx.stroke();

  const bodyY = headY + hr + 18 * sy;

  // Torso
  const torsoW = 168 * sx;
  const torsoH = 214 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, headX - torsoW / 2, bodyY, torsoW, torsoH, 22 * sy);
  ctx.stroke();

  // Legs
  const legSpread = 41 * sx;
  const legW = 56 * sx;
  const legH = 321 * sy;
  const legY = bodyY + torsoH + 6 * sy;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    roundedRectPath(ctx, headX + side * legSpread - legW / 2, legY, legW, legH, 18 * sy);
    ctx.stroke();
  }

  // Arms — angled 30° outward
  const armOffX  = 60 * sx;
  const armW     = 62 * sx;
  const armH     = 290 * sy;
  const armAngle = 30 * (Math.PI / 180);
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(headX + side * armOffX, bodyY);
    ctx.rotate(-side * armAngle);
    ctx.beginPath();
    roundedRectPath(ctx, -armW / 2, 0, armW, armH, 18 * sy);
    ctx.stroke();
    ctx.restore();
  }
}

export function drawSideSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult = 1,
  sideFatnessMult = 1,
  spineX?: number
) {
  const sx = scale * sideFatnessMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;
  const headCX = headX - hr * 0.38;
  const headCY = headY;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Head
  ctx.beginPath();
  ctx.arc(headCX, headCY, hr, 0, Math.PI * 2);
  ctx.stroke();

  // Nose indicator
  const noseTipX  = headCX + hr + 28 * sy;
  const noseTipY  = headCY;
  const noseBaseX = headCX + hr - 6 * sy;
  const noseHalfW = 16 * sy;
  ctx.beginPath();
  ctx.moveTo(noseBaseX, noseTipY - noseHalfW);
  ctx.lineTo(noseTipX, noseTipY);
  ctx.lineTo(noseBaseX, noseTipY + noseHalfW);
  ctx.stroke();

  const bodyW = 145 * sx;
  const bodyCenterX = spineX ?? headX - 40 * sy;
  const bodyX = bodyCenterX - bodyW / 2;
  const bodyY = headCY + hr + 4 * sy;
  const bodyR = 26 * sy;
  const torsoH = 201 * sy;

  ctx.beginPath();
  roundedRectPath(ctx, bodyX, bodyY, bodyW, torsoH, bodyR);
  ctx.stroke();

  const legW = bodyW * 0.78;
  const legH = 291 * sy;
  const legX = bodyCenterX - legW / 2;
  const legY = bodyY + torsoH + 6 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, legX, legY, legW, legH, 20 * sy);
  ctx.stroke();

  const armW = 62 * sx;
  const armH = 261 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, bodyCenterX - armW / 2, bodyY, armW, armH, 16 * sy);
  ctx.stroke();
}

// ---------------------------------------------------------------------------
// Main guidance overlay
// ---------------------------------------------------------------------------

const FIG_H_FRONT     = 651;
const FIG_H_SIDE      = 594;
const FIG_HEAD_TOP    = 46;
const BETA_TOL_DRAW   = 7.0;
const GAMMA_TOL_DRAW  = 10.0;

function keypointsInsideGuidanceBox(
  kpPx: [number, number][],
  natW: number,
  natH: number,
  view: "front" | "right"
) {
  const marginH = 0.05;
  const marginW = view === "front" ? 0.05 : 0.25;
  const x0 = marginW * natW;
  const x1 = (1 - marginW) * natW;
  const y0 = marginH * natH;
  const y1 = (1 - marginH) * natH;
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  return idx.every((i) => {
    const p = kpPx[i];
    if (!p) return false;
    return p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1;
  });
}

export function drawGuidanceOverlay(
  ctx: CanvasRenderingContext2D,
  kpPx: [number, number][],
  view: "front" | "right",
  natW: number,
  natH: number,
  poseOk: boolean,
  headRadiusMult = 1,
  torsoWidthMult = 1,
  sideFatnessMult = 1,
  flipped = false
) {
  const FIG_H = view === "front" ? FIG_H_FRONT : FIG_H_SIDE;

  const scaleMax = 0.90 * natH / FIG_H;
  const scaleMin = 0.50 * natH / FIG_H;

  const centeredHeadY = (s: number) => natH / 2 - (FIG_H / 2 - FIG_HEAD_TOP) * s;

  const coreIdx    = [0, 5, 6, 11, 12, 15, 16];
  const clipMargin = 0.03;
  const bodyDetected = coreIdx.every(i => {
    const p = kpPx[i];
    return Array.isArray(p)
      && Number.isFinite(p[0]) && Number.isFinite(p[1])
      && p[0] > clipMargin * natW && p[0] < (1 - clipMargin) * natW
      && p[1] > clipMargin * natH && p[1] < (1 - clipMargin) * natH;
  });

  const [anchorX, anchorY] = getHeadAnchor(kpPx);
  const bbox           = getPoseBBox(kpPx);
  const outlineTargetH = bbox.height * (view === "right" ? 1.26 : 1.16);
  const rawScale       = outlineTargetH / FIG_H;

  let headX: number, headY: number, scale: number, message: string | null = null;
  const cx = natW / 2;

  if (!bodyDetected) {
    scale   = scaleMax;
    headX   = cx;
    headY   = centeredHeadY(scale);
    message = rawScale > scaleMax ? "Step back" : "Stay in center";
  } else {
    scale   = Math.max(scaleMin, Math.min(scaleMax, rawScale));
    const cy = centeredHeadY(scale);
    headX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, anchorX));
    headY = Math.max(cy - 0.25 * natH, Math.min(cy + 0.25 * natH, anchorY));
  }

  const silhouetteColor = poseOk
    ? "rgba(34,197,94,0.95)"
    : "rgba(245,158,11,0.95)";

  if (view === "right") {
    let spineX: number | undefined;
    if (bodyDetected) {
      const s5 = kpPx[5]; const s6 = kpPx[6];
      if (s5 && s6 && Number.isFinite(s5[0]) && Number.isFinite(s6[0])) {
        const raw = (s5[0] + s6[0]) / 2;
        spineX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, raw));
      }
    }
    const needsFlip = flipped;
    if (needsFlip) {
      ctx.save();
      ctx.translate(natW, 0);
      ctx.scale(-1, 1);
    }
    drawSideSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, sideFatnessMult, spineX);
    if (needsFlip) ctx.restore();
  } else {
    drawFrontSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, torsoWidthMult);
  }

  if (message) {
    const fontSize = Math.round(natH * 0.045);
    ctx.save();
    ctx.font           = `bold ${fontSize}px sans-serif`;
    ctx.textAlign      = "center";
    ctx.textBaseline   = "middle";
    ctx.lineWidth      = fontSize * 0.15;
    ctx.strokeStyle    = "rgba(0,0,0,0.7)";
    ctx.fillStyle      = "rgba(239,68,68,1)";
    ctx.strokeText(message, natW / 2, natH * 0.06);
    ctx.fillText(message,   natW / 2, natH * 0.06);
    ctx.restore();
  }
}
